import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { ChallengeService, ChallengeData } from './challenge.service';
import {
  Habit, HabitCompletion, JournalEntry, Countdown, MigrationGuard,
  MandalaPrefs, RingState, TierName, CategoryStyle,
} from './challenge.model';
import { computeRingState } from './mandala/ring-state';

@Component({
    selector: 'app-challenge',
    templateUrl: './challenge.component.html',
    styleUrls: ['./challenge.component.scss'],
    standalone: false
})
export class ChallengeComponent implements OnInit, OnDestroy {
  private static readonly LAST_SEEN_KEY = 'challenge.lastSeenDate';
  userId: string | null = null;
  loading = true;
  migrating = false;
  migrationSummary: string | null = null;

  habits: Habit[] = [];
  completions: HabitCompletion[] = [];
  journals: { [date: string]: JournalEntry } = {};
  countdown: Countdown | null = null;
  mandalaPrefs: MandalaPrefs | null = null;
  rings: RingState[] = [];
  pulse: { category: string; at: number } | null = null;
  tierUp: { category: string; tier: TierName; at: number } | null = null;
  private tierUpTimer: ReturnType<typeof setTimeout> | null = null;
  decayOverlay: string | null = null;
  private decayTimer: ReturnType<typeof setTimeout> | null = null;
  private rolloverInterval: ReturnType<typeof setInterval> | null = null;
  private previousTier: { [category: string]: TierName } = {};
  private firstEmission = true;
  private pulseTimer: ReturnType<typeof setTimeout> | null = null;

  streakByHabitId: { [habitId: string]: number } = {};
  activeFilter: string | null = null;

  showEditModal = false;
  editHabit: Habit | null = null;
  showHistoryModal = false;
  showMandalaSettings = false;

  editingCountdown = false;
  countdownDraftDate = '';
  countdownDraftLabel = '';

  private authSub?: Subscription;
  private dataSub?: Subscription;

  constructor(
    private auth: AngularFireAuth,
    private service: ChallengeService
  ) {}

  ngOnInit() {
    this.authSub = this.auth.user.subscribe(async user => {
      this.userId = user?.uid ?? null;
      if (!this.userId) { this.loading = false; return; }

      // Reset mandala tier-up baseline when the active user changes.
      this.previousTier = {};
      this.firstEmission = true;

      this.dataSub?.unsubscribe();
      this.dataSub = this.service.subscribeAll(this.userId).subscribe(data => {
        this.applyData(data);
        this.loading = false;
        if (!data.migration && !this.migrating) {
          this.runMigration(data.migration);
        }
      });
    });
    this.checkDayRollover();
    this.rolloverInterval = setInterval(() => this.checkDayRollover(), 60_000);
  }

  ngOnDestroy() {
    this.authSub?.unsubscribe();
    this.dataSub?.unsubscribe();
    if (this.rolloverInterval) clearInterval(this.rolloverInterval);
    if (this.pulseTimer) clearTimeout(this.pulseTimer);
    if (this.tierUpTimer) clearTimeout(this.tierUpTimer);
    if (this.decayTimer) clearTimeout(this.decayTimer);
  }

  private applyData(data: ChallengeData) {
    this.habits = data.habits;
    this.completions = data.completions;
    this.journals = data.journals;
    this.countdown = data.countdown;
    this.mandalaPrefs = data.mandalaPrefs;
    this.recomputeStreaks();
    this.recomputeRings();
  }

  private checkDayRollover() {
    const today = this.todayDate();
    const last = localStorage.getItem(ChallengeComponent.LAST_SEEN_KEY);
    if (!last) {
      localStorage.setItem(ChallengeComponent.LAST_SEEN_KEY, today);
      return;
    }
    if (last === today) return;

    // Compute calendar days between `last` and `today`
    const lastDate = new Date(last + 'T00:00:00');
    const nowDate = new Date(today + 'T00:00:00');
    const missedDays = Math.max(0, Math.round((nowDate.getTime() - lastDate.getTime()) / 86400000) - 1);

    localStorage.setItem(ChallengeComponent.LAST_SEEN_KEY, today);

    if (missedDays > 0) {
      this.decayOverlay = 'Welcome back — your rings settled';
      if (this.decayTimer) clearTimeout(this.decayTimer);
      this.decayTimer = setTimeout(() => { this.decayOverlay = null; this.decayTimer = null; }, 2200);
    }
    // Date cursor advanced; rebaseline the tier-up detector.
    this.firstEmission = true;
    this.recomputeRings();
  }

  private recomputeRings() {
    const previous = { ...this.previousTier };
    this.rings = computeRingState(this.habits, this.completions, this.mandalaPrefs, this.todayDate());

    if (this.firstEmission) {
      for (const r of this.rings) this.previousTier[r.category] = r.tier;
      this.firstEmission = false;
      return;
    }

    for (const r of this.rings) {
      const before = previous[r.category];
      if (before && this.tierIndex(r.tier) > this.tierIndex(before)) {
        this.fireTierUp(r.category, r.tier);
      }
      this.previousTier[r.category] = r.tier;
    }
  }

  private tierIndex(t: TierName): number {
    return ['seed', 'sprout', 'bud', 'bloom', 'flourish', 'mastery'].indexOf(t);
  }

  private fireTierUp(category: string, tier: TierName) {
    if (this.tierUpTimer) clearTimeout(this.tierUpTimer);
    this.tierUp = { category, tier, at: Date.now() };
    this.tierUpTimer = setTimeout(() => { this.tierUp = null; this.tierUpTimer = null; }, 1800);
  }

  private async runMigration(currentGuard: MigrationGuard | null) {
    if (!this.userId) return;
    this.migrating = true;
    try {
      const result = await this.service.runMigrationIfNeeded(this.userId, currentGuard);
      if (result) {
        this.migrationSummary = `Brought over ${result.habits} habits and ${result.completions} completions.`;
        setTimeout(() => { this.migrationSummary = null; }, 6000);
      }
    } catch (err) {
      console.error('Migration failed', err);
      this.migrationSummary = 'Migration failed — your old data is untouched. See console for details.';
    } finally {
      this.migrating = false;
    }
  }

  private recomputeStreaks() {
    this.streakByHabitId = {};
    const today = this.todayDate();
    for (const habit of this.habits) {
      if (habit.frequency !== 'daily') {
        this.streakByHabitId[habit.id] = 0;
        continue;
      }
      const datesSet = new Set(
        this.completions.filter(c => c.habitId === habit.id).map(c => c.date)
      );
      let streak = 0;
      let cursor = today;
      while (datesSet.has(cursor)) {
        streak++;
        cursor = this.shiftDate(cursor, -1);
      }
      this.streakByHabitId[habit.id] = streak;
    }
  }

  private todayDate(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private shiftDate(date: string, deltaDays: number): string {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() + deltaDays);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  // ── Getters for child components ─────────────────────────────────

  get today(): string { return this.todayDate(); }

  get activeHabits(): Habit[] {
    return this.habits.filter(h => !h.archivedAt);
  }

  get archivedHabits(): Habit[] {
    return this.habits.filter(h => h.archivedAt);
  }

  get availableCategories(): string[] {
    const set = new Set<string>();
    for (const h of this.activeHabits) {
      if (h.category) set.add(h.category);
    }
    return Array.from(set).sort();
  }

  get filteredHabits(): Habit[] {
    const base = this.activeFilter
      ? this.activeHabits.filter(h => h.category === this.activeFilter)
      : this.activeHabits;
    return [...base].sort((a, b) => {
      // Uncategorized habits sink to the bottom.
      const aCat = a.category || '￿';
      const bCat = b.category || '￿';
      const catCmp = aCat.localeCompare(bCat, undefined, { sensitivity: 'base' });
      if (catCmp !== 0) return catCmp;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }

  get dailyHabits(): Habit[] {
    return this.filteredHabits.filter(h => h.frequency === 'daily');
  }

  get weeklyHabits(): Habit[] {
    return this.filteredHabits.filter(h => h.frequency === 'weekly');
  }

  todayCompletionFor(habit: Habit): HabitCompletion | null {
    return this.completions.find(c => c.habitId === habit.id && c.date === this.today) ?? null;
  }

  last7DaysFor(habit: Habit): { date: string, completed: boolean }[] {
    const result: { date: string, completed: boolean }[] = [];
    const datesSet = new Set(
      this.completions.filter(c => c.habitId === habit.id).map(c => c.date)
    );
    let cursor = this.shiftDate(this.today, -6);
    for (let i = 0; i < 7; i++) {
      result.push({ date: cursor, completed: datesSet.has(cursor) });
      cursor = this.shiftDate(cursor, 1);
    }
    return result;
  }

  weekCompletionsFor(habit: Habit): HabitCompletion[] {
    const today = new Date(this.today + 'T00:00:00');
    const day = today.getDay(); // 0 = Sun
    const offsetToMonday = (day + 6) % 7;
    const monday = new Date(today);
    monday.setDate(today.getDate() - offsetToMonday);
    const mondayStr = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
    const sundayStr = this.shiftDate(mondayStr, 6);
    return this.completions.filter(c =>
      c.habitId === habit.id && c.date >= mondayStr && c.date <= sundayStr
    );
  }

  get dailyProgress(): { complete: number, total: number } {
    const total = this.dailyHabits.length;
    const complete = this.dailyHabits.filter(h => this.todayCompletionFor(h) !== null).length;
    return { complete, total };
  }

  get weeklyProgress(): { complete: number, total: number } {
    let complete = 0, total = 0;
    for (const h of this.weeklyHabits) {
      total += h.target ?? 0;
      complete += this.weekCompletionsFor(h).length;
    }
    return { complete: Math.min(complete, total), total };
  }

  get todayJournal(): JournalEntry | null {
    return this.journals[this.today] ?? null;
  }

  get daysToTarget(): number | null {
    if (!this.countdown?.targetDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(this.countdown.targetDate + 'T00:00:00');
    const diff = Math.ceil((target.getTime() - today.getTime()) / 86400000);
    return Math.max(0, diff);
  }

  get countdownTitle(): string {
    const days = this.daysToTarget;
    if (days === null) return 'Challenge';
    const label = this.countdown?.label?.trim();
    const noun = days === 1 ? 'day' : 'days';
    return label ? `${days} ${noun} to ${label}` : `${days} ${noun} to target`;
  }

  // ── Event handlers (wired in Task 13) ───────────────────────────

  private firePulse(category: string) {
    if (this.pulseTimer) clearTimeout(this.pulseTimer);
    this.pulse = { category, at: Date.now() };
    this.pulseTimer = setTimeout(() => { this.pulse = null; this.pulseTimer = null; }, 800);
  }

  setFilter(category: string | null) { this.activeFilter = category; }

  openAddHabit() {
    this.editHabit = null;
    this.showEditModal = true;
  }

  openEditHabit(habit: Habit) {
    this.editHabit = habit;
    this.showEditModal = true;
  }

  closeEditModal() {
    this.showEditModal = false;
    this.editHabit = null;
  }

  openHistory() { this.showHistoryModal = true; }
  closeHistory() { this.showHistoryModal = false; }

  openMandalaSettings() { this.showMandalaSettings = true; }
  closeMandalaSettings() { this.showMandalaSettings = false; }

  async onRingOrderChange(order: string[]) {
    if (!this.userId) return;
    const prefs: Omit<MandalaPrefs, 'updatedAt'> = {
      ringOrder: order,
      styles: this.mandalaPrefs?.styles ?? {},
      visibleCategories: this.mandalaPrefs?.visibleCategories,
    };
    await this.service.setMandalaPrefs(this.userId, prefs);
  }

  async onStyleChange(payload: { category: string; style: CategoryStyle }) {
    if (!this.userId) return;
    const prevStyles = this.mandalaPrefs?.styles ?? {};
    const prefs: Omit<MandalaPrefs, 'updatedAt'> = {
      ringOrder: this.mandalaPrefs?.ringOrder ?? [],
      styles: { ...prevStyles, [payload.category]: payload.style },
      visibleCategories: this.mandalaPrefs?.visibleCategories,
    };
    await this.service.setMandalaPrefs(this.userId, prefs);
  }

  async onVisibleCategoriesChange(cats: string[]) {
    if (!this.userId) return;
    const prefs: Omit<MandalaPrefs, 'updatedAt'> = {
      ringOrder: this.mandalaPrefs?.ringOrder ?? [],
      styles: this.mandalaPrefs?.styles ?? {},
      visibleCategories: cats,
    };
    await this.service.setMandalaPrefs(this.userId, prefs);
  }

  async onResetMandala() {
    if (!this.userId) return;
    const prefs: Omit<MandalaPrefs, 'updatedAt'> = {
      ringOrder: [],
      styles: {},
    };
    await this.service.setMandalaPrefs(this.userId, prefs);
  }

  async onToggleCompletion(habit: Habit) {
    if (!this.userId) return;
    const existing = this.todayCompletionFor(habit);
    await this.service.toggleCompletion(this.userId, habit, this.today, existing);
    if (!existing && habit.category) this.firePulse(habit.category);
  }

  async onWeeklyToggle(habit: Habit) {
    if (!this.userId) return;
    await this.service.addCompletion(this.userId, habit.id, this.today);
    if (habit.category) this.firePulse(habit.category);
  }

  async onRemoveWeeklyCompletion(completionId: string) {
    if (!this.userId) return;
    await this.service.removeCompletion(this.userId, completionId);
  }

  async onNoteChange(completionId: string, note: string) {
    if (!this.userId) return;
    await this.service.updateCompletionNote(this.userId, completionId, note);
  }

  async onJournalChange(patch: Partial<Omit<JournalEntry, 'date'>>) {
    if (!this.userId) return;
    await this.service.upsertJournal(this.userId, this.today, patch);
  }

  async onCountdownSave(countdown: Countdown) {
    if (!this.userId) return;
    await this.service.setCountdown(this.userId, countdown);
  }

  async onCountdownClear() {
    if (!this.userId) return;
    await this.service.clearCountdown(this.userId);
  }

  startCountdownEdit() {
    this.countdownDraftDate = this.countdown?.targetDate ?? '';
    this.countdownDraftLabel = this.countdown?.label ?? '';
    this.editingCountdown = true;
  }

  cancelCountdownEdit() { this.editingCountdown = false; }

  async saveCountdownEdit() {
    if (!this.countdownDraftDate) return;
    await this.onCountdownSave({
      targetDate: this.countdownDraftDate,
      label: this.countdownDraftLabel.trim() || undefined,
    });
    this.editingCountdown = false;
  }

  async removeCountdownEdit() {
    await this.onCountdownClear();
    this.editingCountdown = false;
  }

  async onHabitSave(payload: Habit | Omit<Habit, 'id' | 'createdAt'>) {
    if (!this.userId) return;
    if ('id' in payload && payload.id) {
      const { id, ...patch } = payload;
      await this.service.updateHabit(this.userId, id, patch);
    } else {
      await this.service.addHabit(this.userId, { ...payload, createdAt: Date.now() } as Omit<Habit, 'id'>);
    }
    this.closeEditModal();
  }

  async onArchive(habitId: string) {
    if (!this.userId) return;
    await this.service.archiveHabit(this.userId, habitId);
  }

  async onUnarchive(habitId: string) {
    if (!this.userId) return;
    await this.service.unarchiveHabit(this.userId, habitId);
  }

  async onHardDelete(habitId: string) {
    if (!this.userId) return;
    await this.service.deleteHabit(this.userId, habitId, this.completions);
    this.closeEditModal();
  }

  // History modal event handlers
  async onHistoricalToggle(habit: Habit, date: string) {
    if (!this.userId) return;
    const existing = this.completions.find(c => c.habitId === habit.id && c.date === date) ?? null;
    await this.service.toggleCompletion(this.userId, habit, date, existing);
  }

  async onHistoricalJournalChange(date: string, patch: Partial<Omit<JournalEntry, 'date'>>) {
    if (!this.userId) return;
    await this.service.upsertJournal(this.userId, date, patch);
  }
}
